import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  makeStyles,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@material-ui/core';
import {
  Description,
  Delete,
  CloudUpload,
  Publish,
} from '@material-ui/icons';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import { toast } from 'react-toastify';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1),
  },
  
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 'bold',
    fontSize: '16px',
    color: theme.palette.text.primary,
  },
  
  uploadButton: {
    textTransform: 'none',
  },
  
  documentItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
    borderRadius: '8px',
    border: '1px solid ' + (theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'),
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#eeeeee',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
  },
  
  documentHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  
  documentInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  
  documentName: {
    fontWeight: 'bold',
    fontSize: '14px',
    color: theme.palette.text.primary,
  },
  
  documentMeta: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  
  documentActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  
  typeChip: {
    height: '24px',
    fontSize: '11px',
  },
  
  sizeChip: {
    backgroundColor: '#2196f3',
    color: 'white',
    height: '20px',
    fontSize: '10px',
  },
  
  activeChip: {
    backgroundColor: '#4caf50',
    color: 'white',
    height: '20px',
    fontSize: '10px',
  },
  
  inactiveChip: {
    backgroundColor: '#9e9e9e',
    color: 'white',
    height: '20px',
    fontSize: '10px',
  },
  
  deleteButton: {
    color: '#d32f2f',
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'rgba(211, 47, 47, 0.1)',
    },
  },
  
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  
  previewText: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    fontStyle: 'italic',
    maxHeight: '60px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  
  uploadDialog: {
    '& .MuiDialog-paper': {
      minWidth: '400px',
    },
  },
  
  fileInput: {
    display: 'none',
  },
}));

const PromptDocumentsManager = ({ promptId, companyId }) => {
  const classes = useStyles();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Cargar documentos al montar o cuando cambie promptId
  useEffect(() => {
    let isMounted = true;
    
    const fetchDocuments = async () => {
      if (!promptId) {
        if (isMounted) {
          setDocuments([]);
        }
        return;
      }
      
      if (isMounted) {
        setLoading(true);
      }
      
      try {
        const { data } = await api.get('/prompt/' + promptId + '/documents');
        if (isMounted) {
          setDocuments(data.documents || []);
        }
      } catch (error) {
        console.error('Error cargando documentos:', error);
        if (isMounted) {
          toastError(error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDocuments();

    return () => {
      isMounted = false;
    };
  }, [promptId]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Validar cada archivo
    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      // Validar por extensión (más confiable que MIME type)
      const fileName = file.name.toLowerCase();
      const ext = fileName.substring(fileName.lastIndexOf('.') + 1);
      const allowedExts = ['pdf', 'docx', 'txt', 'md', 'markdown'];
      
      if (!allowedExts.includes(ext)) {
        errors.push(`${file.name}: extensión no permitida`);
        return;
      }

      // Validar MIME type (pero ser más flexible con .md)
      const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'text/markdown',
        'application/octet-stream', // Algunos navegadores detectan .md así
        '' // Algunos navegadores no detectan MIME type
      ];
      
      // Si la extensión es válida pero el MIME type no está en la lista, aún permitirlo
      // (especialmente para archivos .md que pueden tener diferentes MIME types)
      if (file.type && !allowedTypes.includes(file.type) && ext !== 'md' && ext !== 'markdown') {
        errors.push(`${file.name}: tipo de archivo no reconocido`);
        return;
      }
      
      // Validar tamaño (10MB máximo)
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name}: excede 10MB`);
        return;
      }

      validFiles.push(file);
    });

    // Mostrar errores si hay
    if (errors.length > 0) {
      toast.error(`Algunos archivos no se pudieron agregar: ${errors.join(', ')}`);
    }

    if (validFiles.length === 0) {
      return;
    }

    // Si solo hay un archivo, mostrar el diálogo de confirmación
    if (validFiles.length === 1) {
      setSelectedFile(validFiles[0]);
      setUploadDialogOpen(true);
    } else {
      // Si hay múltiples archivos, subirlos directamente
      handleMultipleUpload(validFiles);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !promptId) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      await api.post('/prompt/' + promptId + '/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Documento subido exitosamente');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      // Limpiar input file
      const fileInput = document.getElementById('file-input-documents');
      if (fileInput) {
        fileInput.value = '';
      }
      fetchDocuments();
    } catch (error) {
      console.error('Error subiendo documento:', error);
      toastError(error);
    } finally {
      setUploading(false);
    }
  };

  const handleMultipleUpload = async (files) => {
    if (!files || files.length === 0 || !promptId) return;

    setUploading(true);
    const uploadedFiles = [];
    const failedFiles = [];

    try {
      // Subir archivos uno por uno
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        try {
          await api.post('/prompt/' + promptId + '/documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadedFiles.push(file.name);
        } catch (error) {
          console.error(`Error subiendo ${file.name}:`, error);
          failedFiles.push(file.name);
        }
      }

      // Limpiar input file
      const fileInput = document.getElementById('file-input-documents');
      if (fileInput) {
        fileInput.value = '';
      }

      // Mostrar resultados
      if (uploadedFiles.length > 0) {
        toast.success(`${uploadedFiles.length} archivo(s) subido(s) exitosamente`);
      }
      if (failedFiles.length > 0) {
        toast.error(`Error al subir: ${failedFiles.join(', ')}`);
      }

      fetchDocuments();
    } catch (error) {
      console.error('Error en subida múltiple:', error);
      toastError(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;

    try {
      await api.delete('/prompt/' + promptId + '/documents/' + documentId);
      toast.success('Documento eliminado exitosamente');
      fetchDocuments();
    } catch (error) {
      console.error('Error eliminando documento:', error);
      toastError(error);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileTypeLabel = (fileType) => {
    const types = {
      pdf: 'PDF',
      docx: 'DOCX',
      txt: 'TXT',
      md: 'MD'
    };
    return types[fileType] || fileType.toUpperCase();
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography className={classes.title}>
          <Description />
          Documentos PDF
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={uploading ? <CircularProgress size={16} /> : <Publish />}
          onClick={() => document.getElementById('file-input-documents')?.click()}
          className={classes.uploadButton}
          disabled={!promptId || uploading}
        >
          {uploading ? 'Subiendo...' : 'Subir Documento(s)'}
        </Button>
        <input
          id="file-input-documents"
          type="file"
          accept=".pdf,.docx,.txt,.md,.markdown"
          multiple
          className={classes.fileInput}
          onChange={handleFileSelect}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : documents.length === 0 ? (
        <Box p={2} style={{ backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
          <Typography variant="body2" color="textSecondary">
            No hay documentos subidos. Sube documentos PDF, DOCX, TXT o MD para que la IA aprenda de ellos.
          </Typography>
        </Box>
      ) : (
        <Box>
          {documents.map((doc) => (
            <Paper key={doc.id} className={classes.documentItem}>
              <Box className={classes.documentHeader}>
                <Box className={classes.documentInfo}>
                  <Description style={{ marginRight: 8 }} />
                  <Box>
                    <Typography className={classes.documentName}>
                      {doc.filename}
                    </Typography>
                    <Box className={classes.documentMeta}>
                      <Chip 
                        label={getFileTypeLabel(doc.fileType)} 
                        size="small" 
                        className={classes.typeChip}
                      />
                      <Chip 
                        label={formatFileSize(doc.fileSizeKb * 1024)} 
                        size="small" 
                        className={classes.sizeChip}
                      />
                      <Chip 
                        label={doc.isActive ? 'Activo' : 'Inactivo'} 
                        size="small" 
                        className={doc.isActive ? classes.activeChip : classes.inactiveChip}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box className={classes.documentActions}>
                  <IconButton
                    size="small"
                    className={classes.deleteButton}
                    onClick={() => handleDelete(doc.id)}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Box>
              {doc.contentPreview && (
                <Typography className={classes.previewText}>
                  {doc.contentPreview}...
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {/* Dialog de confirmación de subida */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => !uploading && setUploadDialogOpen(false)}
        className={classes.uploadDialog}
      >
        <DialogTitle>Subir Documento</DialogTitle>
        <DialogContent>
          <Typography>
            Archivo seleccionado: <strong>{selectedFile?.name}</strong>
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
            Tamaño: {selectedFile ? formatFileSize(selectedFile.size) : 'N/A'}
          </Typography>
          <Box p={2} style={{ backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9', marginTop: 16 }}>
            <Typography variant="body2" color="textSecondary">
              El sistema extraerá automáticamente el texto del documento para que la IA pueda aprender de él.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setUploadDialogOpen(false)} 
            disabled={uploading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            color="primary"
            variant="contained"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
          >
            {uploading ? 'Subiendo...' : 'Subir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptDocumentsManager;
